import { useState } from 'react';
import {
  PERMISSION_LABELS,
  Permission,
  decodePermissions,
  encodePermissions,
  type PermissionName,
} from '@callzone/shared';
import { api } from '../lib/api';
import { useActiveGuild, useStore } from '../store';

const PERMISSION_ORDER: PermissionName[] = [
  'VIEW_CHANNEL',
  'SEND_MESSAGES',
  'MANAGE_MESSAGES',
  'CONNECT',
  'SPEAK',
  'SHARE_SCREEN',
  'VIDEO',
  'MUTE_MEMBERS',
  'MOVE_MEMBERS',
  'KICK_MEMBERS',
  'CREATE_INVITE',
  'MANAGE_CHANNELS',
  'MANAGE_ROLES',
  'MANAGE_GUILD',
  'ADMINISTRATOR',
];

const COLORS = ['#5865F2', '#57F287', '#FEE75C', '#EB459E', '#ED4245', '#00A8FC', '#F47B67', '#9B84EE'];

export function RolesModal({ onClose }: { onClose: () => void }) {
  const guild = useActiveGuild();
  const toast = useStore((s) => s.toast);
  const [selectedId, setSelectedId] = useState<string | null>(guild?.roles[0]?.id ?? null);
  const [busy, setBusy] = useState(false);

  if (!guild) return null;
  const role = guild.roles.find((r) => r.id === selectedId) ?? guild.roles[0];
  const permissions = decodePermissions(role?.permissions);

  async function guarded(action: () => Promise<unknown>, message?: string) {
    setBusy(true);
    try {
      await action();
      if (message) toast(message);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Acao recusada.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function togglePermission(name: PermissionName) {
    if (!role) return;
    const bit = Permission[name];
    const next = (permissions & bit) === bit ? permissions & ~bit : permissions | bit;
    await guarded(() => api.updateRole(role.id, { permissions: encodePermissions(next) }));
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Cargos e permissoes</h2>
          <button className="icon-btn" onClick={onClose}>
            {'✕'}
          </button>
        </div>

        <div className="modal-body" style={{ display: 'flex', gap: 16, minHeight: 380 }}>
          <div style={{ width: 190, flexShrink: 0 }}>
            {guild.roles.map((item) => (
              <button
                key={item.id}
                className={`channel-item ${item.id === role?.id ? 'active' : ''}`}
                onClick={() => setSelectedId(item.id)}
              >
                <span className="dot" style={{ background: item.color ?? 'var(--text-dim)', width: 10, height: 10, borderRadius: '50%' }} />
                <span className="name">{item.name}</span>
              </button>
            ))}
            <button
              className="btn ghost small full"
              style={{ marginTop: 10, width: '100%' }}
              disabled={busy}
              onClick={() => {
                const name = window.prompt('Nome do novo cargo:');
                if (!name?.trim()) return;
                void guarded(
                  () =>
                    api.createRole(guild.guild.id, {
                      name: name.trim(),
                      permissions: encodePermissions(
                        Permission.VIEW_CHANNEL | Permission.SEND_MESSAGES | Permission.CONNECT | Permission.SPEAK,
                      ),
                    }),
                  'Cargo criado.',
                );
              }}
            >
              + Novo cargo
            </button>
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            {role && (
              <>
                <div className="field">
                  <label>Nome</label>
                  <input
                    defaultValue={role.name}
                    key={role.id}
                    disabled={role.isDefault || busy}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value && value !== role.name) {
                        void guarded(() => api.updateRole(role.id, { name: value }));
                      }
                    }}
                  />
                  {role.isDefault && (
                    <div className="hint">
                      O @everyone vale para todos os membros e nao pode ser renomeado nem apagado.
                    </div>
                  )}
                </div>

                {!role.isDefault && (
                  <div className="field">
                    <label>Cor</label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {COLORS.map((color) => (
                        <button
                          key={color}
                          onClick={() => void guarded(() => api.updateRole(role.id, { color }))}
                          style={{
                            width: 26,
                            height: 26,
                            borderRadius: '50%',
                            background: color,
                            border: role.color === color ? '2px solid #fff' : '2px solid transparent',
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {!role.isDefault && (
                  <div className="perm-row" style={{ paddingLeft: 0 }}>
                    <span className="perm-name">Mostrar membros separados na lista</span>
                    <button
                      className={`switch ${role.hoist ? 'on' : ''}`}
                      disabled={busy}
                      onClick={() => void guarded(() => api.updateRole(role.id, { hoist: !role.hoist }))}
                    />
                  </div>
                )}

                <div className="member-group-title" style={{ paddingLeft: 0, marginTop: 8 }}>
                  Permissoes
                </div>
                <div className="perm-grid">
                  {PERMISSION_ORDER.map((name) => {
                    const bit = Permission[name];
                    const enabled = (permissions & bit) === bit;
                    return (
                      <div key={name} className="perm-row">
                        <span className="perm-name">
                          {PERMISSION_LABELS[name]}
                          {name === 'ADMINISTRATOR' && (
                            <span style={{ color: 'var(--yellow)', fontSize: 11, marginLeft: 8 }}>
                              ignora todas as outras
                            </span>
                          )}
                        </span>
                        <button
                          className={`switch ${enabled ? 'on' : ''}`}
                          disabled={busy}
                          onClick={() => void togglePermission(name)}
                        />
                      </div>
                    );
                  })}
                </div>

                {!role.isDefault && (
                  <button
                    className="btn danger small"
                    style={{ marginTop: 16 }}
                    disabled={busy}
                    onClick={() => {
                      if (!window.confirm(`Apagar o cargo "${role.name}"?`)) return;
                      void guarded(async () => {
                        await api.deleteRole(role.id);
                        setSelectedId(null);
                      }, 'Cargo apagado.');
                    }}
                  >
                    Apagar cargo
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
